/**
 * @file Converts raw competition manifests into the normalized {@link Competition}
 * domain model. This is the single place that understands the on-the-wire
 * schema; everything else works with typed models.
 *
 * The real (Backblaze) manifest is nested + snake_case:
 *   { manifest_version, competition: { season, week, title, theme, description,
 *     generated_at, id }, assets: { skybox:{url}, track:{url,piece_count},
 *     loading_screen:{url}, music:{title,genre,duration_seconds,tempo_bpm,url} } }
 * Asset URLs are absolute. We also stay tolerant of a flat legacy shape.
 */

/**
 * @typedef {import('../models/manifest').Competition} Competition
 * @typedef {import('../models/manifest').CompetitionSummary} CompetitionSummary
 * @typedef {import('../models/manifest').Skybox} Skybox
 * @typedef {import('../models/manifest').MusicMetadata} MusicMetadata
 * @typedef {import('../models/manifest').TrackStatistics} TrackStatistics
 */

/** @param {*} value @returns {Date|null} */
function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** @param {*} value @returns {number|null} */
function toNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** @param {*} value @returns {string|null} */
function toStringOrNull(value) {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** First defined/non-empty value among the arguments. */
function firstDefined(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/** The competition metadata block (nested `competition`, or the root itself). */
function competitionBlock(raw) {
  return raw && typeof raw.competition === 'object' && raw.competition
    ? raw.competition
    : raw;
}

/** The assets block (nested `assets`, or the root itself for legacy shape). */
function assetsBlock(raw) {
  return raw && typeof raw.assets === 'object' && raw.assets ? raw.assets : raw;
}

/**
 * @param {*} raw
 * @returns {Skybox}
 */
function normalizeSkybox(raw) {
  const assets = assetsBlock(raw);
  const sb = assets.skybox || {};
  const url = firstDefined(sb.url, sb.artworkUrl) || '';
  const credit = firstDefined(
    sb.credit,
    sb.provider && sb.provider.name
  );
  return {
    name: firstDefined(sb.name, 'Skybox') + '',
    artworkUrl: url,
    thumbnailUrl: firstDefined(sb.thumbnailUrl, url) || '',
    credit: toStringOrNull(credit),
  };
}

/**
 * @param {*} raw
 * @returns {string|null}
 */
function normalizeLoadingImage(raw) {
  const assets = assetsBlock(raw);
  const candidates = [
    assets.loading_screen && assets.loading_screen.url,
    assets.loadingScreen && assets.loadingScreen.url,
    assets.loading && assets.loading.url,
    raw.loadingImageUrl,
    raw.loadingImage,
    typeof assets.loading === 'string' ? assets.loading : null,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return null;
}

/**
 * @param {*} raw
 * @returns {MusicMetadata|null}
 */
function normalizeMusic(raw) {
  const assets = assetsBlock(raw);
  const m = assets.music || raw.music;
  if (!m || typeof m !== 'object') return null;
  return {
    title: firstDefined(m.title, 'Unknown Track') + '',
    artist:
      firstDefined(m.artist, m.genre, m.provider && m.provider.name, '') + '',
    album: toStringOrNull(firstDefined(m.subgenre, m.album)),
    bpm: toNumber(firstDefined(m.tempo_bpm, m.bpm)),
    durationSeconds: toNumber(firstDefined(m.duration_seconds, m.durationSeconds)),
    previewUrl: toStringOrNull(firstDefined(m.url, m.previewUrl)),
    license: toStringOrNull(m.license),
  };
}

/**
 * @param {*} raw
 * @returns {TrackStatistics|null}
 */
function normalizeTrack(raw) {
  const assets = assetsBlock(raw);
  const t = assets.track || raw.track;
  if (!t || typeof t !== 'object') return null;
  return {
    name: toStringOrNull(t.name),
    trackUrl: toStringOrNull(t.url),
    pieceCount: toNumber(firstDefined(t.piece_count, t.pieceCount)),
    lengthMeters: toNumber(t.lengthMeters),
    checkpoints: toNumber(t.checkpoints),
    laps: toNumber(t.laps),
    difficulty: toStringOrNull(t.difficulty),
    surface: toStringOrNull(t.surface),
    hazards: toNumber(t.hazards),
    bestTimeSeconds: toNumber(t.bestTimeSeconds),
  };
}

/**
 * Normalize a raw manifest into the domain {@link Competition} model.
 *
 * @param {*} raw
 * @param {Object} [ctx]
 * @param {string} [ctx.sourceUrl]  URL the manifest was fetched from.
 * @returns {Competition}
 */
export function normalizeCompetition(raw, ctx = {}) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Competition manifest is empty or malformed.');
  }

  const comp = competitionBlock(raw);
  const season = toNumber(comp.season);
  const week = toNumber(comp.week);
  if (season === null || week === null) {
    throw new Error('Competition manifest is missing season/week.');
  }

  // Canonical id matches the archive index / routing scheme (e.g. "s1-w3"),
  // not the manifest's internal id (e.g. "season01-week03").
  const id = `s${season}-w${week}`;

  return {
    schemaVersion: toNumber(firstDefined(raw.manifest_version, raw.schemaVersion)) || 1,
    season,
    week,
    id,
    title: firstDefined(comp.title, `Season ${season}, Week ${week}`) + '',
    theme: firstDefined(comp.theme, 'Untitled Theme') + '',
    description: firstDefined(comp.description, '') + '',
    generatedAt: toDate(firstDefined(comp.generated_at, comp.generatedAt, raw.generatedAt)),
    opensAt: toDate(firstDefined(comp.opens_at, raw.opensAt)),
    closesAt: toDate(firstDefined(comp.closes_at, raw.closesAt)),
    nextCompetitionAt: toDate(firstDefined(comp.next_competition_at, raw.nextCompetitionAt)),
    skybox: normalizeSkybox(raw),
    music: normalizeMusic(raw),
    track: normalizeTrack(raw),
    loadingImageUrl: normalizeLoadingImage(raw),
    playUrl: (raw.play && raw.play.url) || '',
    manifestUrl: firstDefined(raw.manifestUrl, ctx.sourceUrl, '') + '',
  };
}

/**
 * Preview image for a competition: the loading screen if present, else the
 * skybox artwork. Used by archive cards.
 * @param {Competition} competition
 * @returns {string}
 */
export function previewImageUrl(competition) {
  return (
    competition.loadingImageUrl ||
    (competition.skybox && competition.skybox.artworkUrl) ||
    ''
  );
}

/**
 * Project a full competition down to the summary used by archive cards.
 * @param {Competition} competition
 * @returns {CompetitionSummary}
 */
export function toSummary(competition) {
  return {
    id: competition.id,
    season: competition.season,
    week: competition.week,
    title: competition.title,
    theme: competition.theme,
    skybox: competition.skybox,
    playUrl: competition.playUrl,
    manifestUrl: competition.manifestUrl,
  };
}
